import { Languages, Loader2 } from 'lucide-react'
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@teach/ui'
import type { Instruction } from '@/features/instructions/types'
import { useArticleModeration } from '@/features/moderation/useArticleModeration'
import type { ArticleForm } from '@/features/instructions/lib/articleDraft'
import type { TranslationStatus } from './useTranslation'
import { translationInDraft } from '@/features/instructions/lib/translationInDraft'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  instruction: Instruction | null
  onTranslate: () => void
  isTranslating: boolean
  translationStatus: TranslationStatus
  /** Статья на проверке: перевод записал бы поля под заявкой. */
  frozen: boolean
  /** Засев формы (живое + черновик): перевод «есть», если он есть в версии автора. */
  seed: ArticleForm
}

type EditorMessages = (typeof messages)['ru']

const statusLabel = (m: EditorMessages, status: TranslationStatus): string | undefined =>
  (
    ({
      pending: m.translatePending,
      running: m.translateRunning,
      retrying: m.translateRetrying,
    }) as Partial<Record<TranslationStatus, string>>
  )[status]

const isTranslationFresh = (
  instruction: Instruction | null,
  stale: boolean,
  seed: ArticleForm,
): boolean =>
  instruction !== null && !stale && Boolean(seed.titleEn?.trim()) && Boolean(seed.contentEn?.trim())

const tooltipText = (
  m: EditorMessages,
  instruction: Instruction | null,
  frozen: boolean,
  fresh: boolean,
  inDraft: boolean,
) => {
  if (frozen) return m.translateFrozen
  if (!instruction) return m.translateSaveFirst
  if (fresh) return inDraft ? m.translationInDraft : m.translationFresh
  return m.translationChanged
}

export const TranslateButton = ({
  instruction,
  onTranslate,
  isTranslating,
  translationStatus,
  frozen,
  seed,
}: Props) => {
  // Свежесть судит сервер по версии «живое + черновик» (`/moderation`); пока ответа нет —
  // по флагу живой статьи.
  const m = useMessages(messages)
  const { state } = useArticleModeration(instruction?.id ?? null)
  const translationStale = state?.translationStale ?? instruction?.translationStale ?? false
  // Устаревший перевод — та же кнопка, только с подписью: клик сразу обновляет перевод.
  const stale = translationStale && !isTranslating
  // Перевод есть и не устарел — переводить нечего (сервер ответил бы «Перевод актуален»).
  const fresh = !isTranslating && isTranslationFresh(instruction, translationStale, seed)
  // Живая статья с неотправленными правками: перевод ещё не у читателей.
  const inDraft = translationInDraft(state)
  const label = statusLabel(m, translationStatus) ?? (stale ? m.translationStale : null)
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="ghost"
              size={label ? 'sm' : 'icon'}
              onClick={onTranslate}
              disabled={isTranslating || !instruction || frozen || fresh}
              className={cn(
                'h-8 gap-1.5 rounded-full',
                stale
                  ? 'bg-warning/10 px-3 text-warning hover:bg-warning/20 hover:text-warning'
                  : null,
              )}
              aria-label={stale ? m.translateStaleAria : m.translate}
            >
              {isTranslating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Languages className="w-4 h-4" />
              )}
              {label ? <span className="text-xs">{label}</span> : null}
            </Button>
          </span>
        </TooltipTrigger>
        {!instruction || frozen || stale || fresh ? (
          <TooltipContent side="bottom">
            {tooltipText(m, instruction, frozen, fresh, inDraft)}
          </TooltipContent>
        ) : null}
      </Tooltip>
    </TooltipProvider>
  )
}
